import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useMemo, useState } from "react";
import CustomSelect from "../../../../../../components/common/Select";
import { MemoryMessage } from "../hooks/useMemoryMessages";
import apiService from "../../../../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../../../../components/common/ShowToast";
import { Contacts } from "@mui/icons-material";

interface Contact {
  email: string;
  name: string;
  source: "generic" | "youtube";
  count: number;
}

interface ContactsTabProps {
  messages: MemoryMessage[];
}

function ContactCard({
  contact,
  checked,
  onCheck,
}: {
  contact: Contact;
  checked: boolean;
  onCheck: () => void;
}) {

  const [isConverting, setIsConverting] = useState(false);

  const initials = (contact.name ?? "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleConvertSingle = async () => {
    setIsConverting(true);
    try {
      const response = await apiService.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>("/api/contacts/contact/bulk-create-from-comments/", {
        comments: [{ author: contact.name, email: contact.email || null }],
      });

      if (response.data.created > 0) {
        showSuccessToast(`${contact.name} added to contacts.`);
      } else {
        showSuccessToast("Contact already exists, skipped.");
      }
    } catch {
      showErrorToast("Failed to convert contact.");
    } finally {
      setIsConverting(false);
    }
  };

  const sourceLabel =
    contact.source === "youtube" ? "Youtube" : "Memorial Page";
  const sourceColor = contact.source === "youtube" ? "#ef4444" : "#707070";
  const sourceBg = contact.source === "youtube" ? "#fef2f2" : "#e3e0e0";

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        p: 2,
        boxShadow: 0,
        bgcolor: "background.paper",
        position: "relative",
        border: "1px solid #f0f4f8",
      }}
    > 
      <Checkbox
        checked={checked}
        onChange={onCheck}
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          p: 0,
          color: "#cbd5e1",
          "&.Mui-checked": { color: "#724e41" },
        }}
      />

      <Tooltip title="Convert to contact">
        <Box
          onClick={handleConvertSingle}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isConverting ? "not-allowed" : "pointer",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          {isConverting
            ? <CircularProgress size={14} />
            : <Contacts sx={{ fontSize: 18, color: "#8E8E8E" }} />
          }
        </Box>
      </Tooltip>


      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1,
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: "#8E8E8E",
            fontSize: "16px",
            fontWeight: 700,
            mb: 1,
          }}
        >
          {initials}
        </Avatar>

        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#1e293b",
            textAlign: "center",
            mb: 0.5,
          }}
        >
          {contact.name || "Anonymous"}
        </Typography>

        <Chip
          label={sourceLabel}
          size="small"
          sx={{
            bgcolor: sourceBg,
            color: sourceColor,
            fontWeight: 600,
            fontSize: "12px",
            height: 18,
            borderRadius: "4px",
            mb: 1.5,
          }}
        />

        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
          <Box
            component="a"
            href={contact.email ? `mailto:${contact.email}` : undefined}
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: contact.email ? "pointer" : "default",
              textDecoration: "none",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function ContactsTab({ messages }: ContactsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [checkedEmails, setCheckedEmails] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState(false);

  // Derive contacts from messages — deduplicate by email
  const contacts = useMemo<Contact[]>(() => {
    const map = new Map<string, Contact>();
    messages.forEach((msg) => {
      const key = msg.sender_email?.toLowerCase() ?? `no-email-${msg.id}`;
      if (map.has(key)) {
        map.get(key)!.count += 1;
      } else {
        map.set(key, {
          email: msg.sender_email ?? "",
          name: msg.sender_full_name ?? "Anonymous",
          source: msg.comment_source,
          count: 1,
        });
      }
    });
    return Array.from(map.values());
  }, [messages]);

  const filtered = useMemo(() => {
    return contacts.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource =
        filterSource === "all" || c.source === filterSource;
      return matchesSearch && matchesSource;
    });
  }, [contacts, searchQuery, filterSource]);

  const toggleCheck = (email: string) => {
    setCheckedEmails((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email],
    );
  };

  const handleConvertToContacts = async () => {
    const selectedContacts = filtered.filter((c) =>
      checkedEmails.includes(c.email)
    );

    if (selectedContacts.length === 0) {
      showErrorToast("Please select contacts to convert");
      return;
    }

    const payload = {
      comments: selectedContacts.map((c) => ({
        author: c.name,
        email: c.email || null,
      })),
    };

    setIsConverting(true);
    try {
      const response = await apiService.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>("/api/contacts/contact/bulk-create-from-comments/", payload);

      showSuccessToast(
        `${response.data.created} contact(s) created, ${response.data.skipped} skipped.`
      );
      setCheckedEmails([]);
    } catch {
      showErrorToast("Failed to convert contacts.");
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2.5, width: "fit-content" }}
      >
        <Box sx={{ width: 200, flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Search here..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
              },
            }}
          />
        </Box>

        <CustomSelect
          onChange={(e) => setFilterSource(String(e.target.value))}
          label=""
          defaultValue="all"
          sx={{ width: 150 }}
          size="small"
        >
          <MenuItem value="all">All Contacts</MenuItem>
          <MenuItem value="generic">Memorial Page</MenuItem>
          <MenuItem value="youtube">Youtube</MenuItem>
        </CustomSelect>

        <CustomSelect
          label="Bulk Actions"
          defaultValue="email"
          sx={{ width: 150 }}
          size="small"
          onChange={() => {}}
        >
          <MenuItem value="email">Email</MenuItem>
        </CustomSelect>

        <Button
          variant="outlined"
          disabled={checkedEmails.length === 0 || isConverting}
          onClick={handleConvertToContacts}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            height: 38,
            width: 140,
            whiteSpace: "nowrap",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {isConverting ? "Converting..." : "Convert to Contacts"}
        </Button>
      </Stack>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>
            No contacts found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {filtered.map((contact) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={contact.email}>
              <ContactCard
                contact={contact}
                checked={checkedEmails.includes(contact.email)}
                onCheck={() => toggleCheck(contact.email)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}